import React, { useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import { to_csv, pii_columns } from './to_csv';
import { capped_text } from './collect_pages';
import './csv_export.css';

export const CSV_FAILED_TEXT = 'Could not download the CSV. Try again.';
export const CSV_EMPTY_TEXT = 'Nothing to download for these filters.';

/*
 * "Download CSV" for a table header/toolbar. Exports what the user is looking at:
 *   rows        — the rows on screen (client-side tables), or
 *   load_rows() — resolves { rows, capped } for server-paged tables (see collect_all_pages).
 * columns: [{ header, key | value(row), kind }] (to_csv). file_name: string or () => string.
 * on_error(e): optional, e.g. route a lost grant to the shell; the message still shows inline.
 */
const DownloadCsvButton = ({
  columns,
  rows,
  load_rows,
  file_name,
  label = 'Download CSV',
  disabled = false,
  on_error,
  className = ''
}) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState({ kind: '', text: '' });
  const busy_ref = useRef(false);

  const save = (list) => {
    const name = typeof file_name === 'function' ? file_name() : file_name;
    saveAs(new Blob([to_csv(columns, list)], { type: 'text/csv;charset=utf-8' }), name);
  };

  const run = () => {
    if (busy_ref.current || disabled) return;
    const pii = pii_columns(columns);
    if (pii.length) {
      // a programming error, never a user one: a column list must not carry PII.
      console.error('DownloadCsvButton: PII columns refused', pii);
      setNote({ kind: 'error', text: CSV_FAILED_TEXT });
      return;
    }
    setNote({ kind: '', text: '' });
    if (!load_rows) {
      if (!rows || !rows.length) setNote({ kind: 'info', text: CSV_EMPTY_TEXT });
      else save(rows);
      return;
    }
    busy_ref.current = true;
    setBusy(true);
    let pending;
    try {
      pending = Promise.resolve(load_rows());
    } catch (e) {
      pending = Promise.reject(e);
    }
    pending
      .then((res) => {
        const list = (res && res.rows) || [];
        if (!list.length) setNote({ kind: 'info', text: CSV_EMPTY_TEXT });
        else {
          save(list);
          if (res.capped) setNote({ kind: 'info', text: capped_text(res.cap) });
        }
      })
      .catch((e) => {
        console.error('DownloadCsvButton: export failed', e);
        setNote({ kind: 'error', text: CSV_FAILED_TEXT });
        if (on_error) on_error(e, CSV_FAILED_TEXT);
      })
      .finally(() => {
        busy_ref.current = false;
        setBusy(false);
      });
  };

  const off = busy || disabled;
  return (
    <span className={`csv-dl ${className}`.trim()}>
      {/* aria-disabled, not disabled: the pressed button keeps focus while it prepares. */}
      <button
        type="button"
        className="csv-dl-btn"
        onClick={run}
        aria-disabled={off ? 'true' : undefined}
        aria-busy={busy ? 'true' : undefined}
      >
        {busy ? 'Preparing…' : `↓ ${label}`}
      </button>
      {note.text ? (
        <span
          className={`csv-dl-note csv-dl-note--${note.kind}`}
          role={note.kind === 'error' ? 'alert' : 'status'}
        >
          {note.text}
        </span>
      ) : null}
    </span>
  );
};

export default DownloadCsvButton;
